'use client';
// LightboxOverlay — THE one full-screen photo viewer (16/10/26, Founder: swipe + bigger image). Shared by
// ImageLightbox (pro page / reviews) and GalleryCarousel (category-page cards) — never copy it.
// - Image fills ~the whole viewport (100vw x 88dvh, object-fit contain); arrows are OVERLAID on the image.
// - Swipe: finger LEFT = next, finger RIGHT = previous. RTL site: forward is to the LEFT (same as the ‹ button on
//   the left and ArrowLeft). Needs >=40px, horizontal-dominant (|dx| >= 1.5|dy|), single finger (pinch-zoom untouched),
//   touch-action pan-y so vertical gestures are never captured; listeners are React's passive touch handlers.
// - Also: Escape closes, ArrowLeft/ArrowRight navigate, body scroll locked while open.
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { trackEvent } from '@/components/Tracker';

/** sizes/width the viewer image is requested with — warm-up code (GalleryCarousel) must use the same pair. */
export const LB_SIZES = '100vw';
export const LB_WIDTH = 960;

type Props = {
  count: number;
  idx: number;
  go: (delta: number) => void;
  close: () => void;
  /** props for the <img> of photo i (src/srcSet/sizes/alt/referrerPolicy…) */
  imgProps: (i: number) => React.ImgHTMLAttributes<HTMLImageElement>;
  style?: React.CSSProperties;
};

export default function LightboxOverlay({ count, idx, go, close, imgProps }: Props) {
  const touch = useRef<{ x: number; y: number; multi: boolean } | null>(null);
  const swiped = useRef(0);

  // engagement signal (19/10/26): the overlay mounts only when a photo is opened -> one 'gallery_open' per open (classic pages)
  useEffect(() => { trackEvent('gallery_open', { metadata: { count, idx } }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') go(1);      // RTL: left = next
      else if (e.key === 'ArrowRight') go(-1);    // right = previous
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [close, go]);

  const onTouchStart = (e: React.TouchEvent) => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY, multi: e.touches.length > 1 }; };
  const onTouchMove = (e: React.TouchEvent) => { if (touch.current && e.touches.length > 1) touch.current.multi = true; };
  const onTouchEnd = (e: React.TouchEvent) => {
    const s = touch.current; touch.current = null;
    if (!s || s.multi || count < 2) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    swiped.current = Date.now();
    go(dx < 0 ? 1 : -1);
  };

  const navBtn: React.CSSProperties = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 2,
    width: 48, height: 48, borderRadius: '50%', border: '1px solid rgba(255,255,255,.35)', cursor: 'pointer',
    background: 'rgba(0,0,0,.5)', color: '#fff', fontSize: 30, lineHeight: 1, boxShadow: '0 2px 10px rgba(0,0,0,.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  return createPortal(
    <div role="dialog" aria-modal="true" data-lightbox="1"
      onClick={() => { if (Date.now() - swiped.current > 400) close(); }}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
      style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', touchAction: 'pan-y pinch-zoom' }}>
      <button onClick={close} type="button" aria-label="סגור"
        style={{ position: 'absolute', top: 12, left: 12, zIndex: 3, width: 44, height: 44, borderRadius: '50%', border: '1px solid rgba(255,255,255,.35)', cursor: 'pointer', background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: 22 }}>✕</button>
      {count > 1 && (
        <button dir="ltr" onClick={e => { e.stopPropagation(); go(-1); }} type="button" aria-label="הקודם" style={{ ...navBtn, right: 10 }}>›</button>
      )}
      <img {...imgProps(idx)} data-lightbox-img="1" draggable={false} onClick={e => e.stopPropagation()}
        style={{ width: '100vw', maxWidth: 1600, height: '88dvh', objectFit: 'contain', userSelect: 'none' }} />
      {count > 1 && (
        <button dir="ltr" onClick={e => { e.stopPropagation(); go(1); }} type="button" aria-label="הבא" style={{ ...navBtn, left: 10 }}>‹</button>
      )}
      {count > 1 && (
        <div dir="ltr" style={{ position: 'absolute', bottom: 14, zIndex: 3, color: 'rgba(255,255,255,.9)', fontSize: 14, fontWeight: 600, background: 'rgba(0,0,0,.5)', padding: '4px 14px', borderRadius: 20 }}>
          {idx + 1} / {count}
        </div>
      )}
    </div>,
    document.body
  );
}
